import SeoLanding from "@/components/SeoLanding";
import { AlertOctagon, Gavel, Timer } from "lucide-react";

export default function SheriffSaleHelp() {
  return (
    <SeoLanding
      slug="sheriff-sale-help"
      crumb="Sheriff Sale Help"
      eyebrow="MMXXVI · Emergency Sheriff-Sale Desk"
      pageTitle="Sheriff Sale Help — Stop or Delay a Sheriff Sale in Days | Azure Gypsy Guild"
      pageDescription="Facing a sheriff sale? Azure Gypsy Guild's emergency sheriff-sale desk triages your case the same day, files a verified counterclaim, and pledges trustee funds to settle the mortgage before the sale date. 98% settlement rate on qualified cases. $1,500/yr + 1.5% at settlement."
      h1="Sheriff sale help. Before the gavel drops."
      subhead="A sheriff-sale date already on the calendar is not the end of the case — it is a deadline. Azure Gypsy Guild's emergency triage desk reviews sheriff-sale intakes the same day, files a verified counterclaim against the law firm and bank, and pledges trustee funds to settle the mortgage before the sale date. When the counterclaim is heard, the sheriff sale is typically halted by the court."
      pillars={[
        { icon: AlertOctagon, value: "24hr",  label: "Emergency triage", desc: "Same-day trustee review on scheduled sales" },
        { icon: Gavel,        value: "Halt",  label: "The sale",         desc: "Court hearing of the counterclaim typically stops the sale" },
        { icon: Timer,        value: "60",    label: "Days to settle",   desc: "Full settlement inside the sixty-day window" },
      ]}
      sections={[
        {
          h: "What actually stops a sheriff sale",
          p: [
            "There are only a few filings a court will typically respect before a scheduled sheriff sale: a bankruptcy filing (which imposes an automatic stay), a temporary restraining order, or a verified counterclaim with a request for injunctive relief. The Guild's process uses the third path — a counterclaim, backed by a good accounting ledger audit and a pledged trustee account — because it does not put a bankruptcy on your record and does not require you to have already retained counsel.",
            "The counterclaim is filed against the law firm and bank inside your existing court case. Because it puts the standing of the note-holder in dispute, the court will typically hear it before allowing the sale to proceed. This is what buys the sixty-day settlement window.",
          ],
          internal: [
            { to: "/stop-foreclosure-fast", label: "The full 60-day timeline" },
            { to: "/mortgage-relief-without-bankruptcy", label: "Why we skip bankruptcy" },
          ],
        },
        {
          h: "How the emergency desk works",
          p: [
            "Send a message through the trustee contact page. Include: the property address, the sale date, the case number, and the name of the law firm on the pleadings. A senior Guild trustee reviews the intake the same business day and, when the case qualifies, opens an emergency file inside the private portal.",
            "The six e-sign agreements, the intake application, and the initial document upload are all completed inside the portal — usually within the first twenty-four hours. The ledger audit begins immediately. The counterclaim is filed with the court, the law firm, and the bank before the sale date whenever the timeline permits.",
          ],
        },
        {
          h: "What documents to have ready",
          p: [
            "The mortgage statement (most recent). The deed or title. Government ID. Every piece of court paperwork you have received — the complaint, any motions, any answer, and the sheriff-sale notice itself. If you have a securitization audit or a prior QWR (qualified written request) response, upload that as well.",
            "You can upload everything from the Member Portal from any phone or tablet. Files go directly into the Emergent trustee vault and are visible only to your assigned trustee and the case-audit team.",
          ],
          internal: [
            { to: "/portal", label: "Go to my portal" },
            { to: "/contact", label: "Contact the emergency desk" },
          ],
        },
        {
          h: "What it costs and when you pay",
          p: [
            "The Guild has exactly two fees, and neither is charged based on how close your sale date is. $1,500 yearly Guild membership grants access to the private portal, the trustee, the e-sign agreements, and the document vault. 1.5% of the mortgage balance is the counterclaim service fee — paid only if the case settles. No settlement, no service fee.",
            "There is no emergency surcharge, no rush fee, and no hourly billing. The counterclaim is filed as fast as the trustee can prepare it, regardless of the calendar.",
          ],
        },
      ]}
      faq={[
        {
          q: "How close to the sale date is too late?",
          a: "The earlier the better, but the trustee will review any active sheriff-sale case up to the day before the scheduled sale. Cases filed inside the last week require prioritized document upload and same-day agreement signing.",
        },
        {
          q: "Will the counterclaim automatically stop the sale?",
          a: "The counterclaim + injunctive relief request is typically respected by the court and the sale is halted while it is heard. This is not a guaranteed outcome — but it is the outcome in the vast majority of qualified cases.",
        },
        {
          q: "Do I need to have hired a lawyer already?",
          a: "No. The Guild trustees prepare and file the counterclaim on your behalf. If your case additionally requires a separate court appearance, we refer to a partner attorney.",
        },
        {
          q: "What if the sale is tomorrow?",
          a: "Message the contact page immediately with the sale date in the subject line. A trustee will respond within business hours the same day. In some cases, an emergency filing can be completed within twenty-four hours of intake.",
        },
        {
          q: "Can I do this from my phone?",
          a: "Yes. The entire member experience — application, six signed agreements, document upload, trustee messaging — is fully mobile and tablet friendly. Members typically complete initial intake from their phone the same day they message the trustee.",
        },
        {
          q: "Does this work if I'm already in bankruptcy?",
          a: "Yes. The counterclaim runs on a parallel track and a senior trustee will coordinate with your bankruptcy counsel.",
        },
      ]}
      ctaTitle="If the sale is on the calendar, the intake starts today."
    />
  );
}
